/**
 * SessionStorage gate for the one-shot artemis-web redirect: once we have sent a
 * flagged-in user to artemis-web, we never send them again for the rest of the tab
 * session. This bounds a potential ping-pong if artemis-web ever bounces a user back
 * (BCP-3617 / BCP-3618: a cross-app Okta `access_denied` can send a user straight back
 * to the app that redirected them), and gives anyone who navigates back to bennie-web
 * on purpose a way to stay there on any later page load in the tab.
 *
 * The marker is per-user, not per-tab, so logout clears it — otherwise the next person
 * to log in in the same tab would inherit the previous user's redirect and never be
 * sent to artemis-web.
 *
 * Storage access is guarded the same way getAnonymousLaunchDarklyUserContext guards it:
 * sessionStorage throws in some blocked-storage configurations, and this gate is a
 * loop guard, not a correctness requirement. Losing it must degrade to "no guard",
 * never to a crash in the app-shell render path.
 */
const ARTEMIS_REDIRECT_GATE_STORAGE_KEY = 'bennie-artemis-redirected';
const ARTEMIS_REDIRECT_GATE_MARKED = '1';

export function markRedirectedToArtemisWeb() {
  try {
    sessionStorage.setItem(
      ARTEMIS_REDIRECT_GATE_STORAGE_KEY,
      ARTEMIS_REDIRECT_GATE_MARKED,
    );
  } catch {
    // Storage unavailable: the redirect still happens, it just is not deduped.
  }
}

export function clearArtemisRedirectGate() {
  try {
    sessionStorage.removeItem(ARTEMIS_REDIRECT_GATE_STORAGE_KEY);
  } catch {
    // Storage unavailable: nothing was ever written, so nothing to clear.
  }
}

export function hasRedirectedToArtemisWeb() {
  try {
    return (
      sessionStorage.getItem(ARTEMIS_REDIRECT_GATE_STORAGE_KEY) ===
      ARTEMIS_REDIRECT_GATE_MARKED
    );
  } catch {
    return false;
  }
}
